import { useState } from 'react';
import useSWR from 'swr';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Thumbnail } from '@/components/domain/Thumbnail';

type Logo = {
  id: string;
  name?: string;
  schoolName?: string;
  sport?: string;
  tags?: string[];
  searchTerms?: string;
  logoPath?: string;
  thumbnailPath?: string;
  usageCount?: number;
  source?: string;
  createdAt?: string;
};
type Payload = { logos: Logo[] };

export default function LogoLibrary() {
  const [q, setQ] = useState('');
  const { data, isLoading } = useSWR<Payload>('/api/logos?limit=200', get);

  const filtered = (data?.logos || []).filter((l) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      String(l.name || '').toLowerCase().includes(needle) ||
      String(l.schoolName || '').toLowerCase().includes(needle) ||
      String(l.sport || '').toLowerCase().includes(needle) ||
      String(l.searchTerms || '').toLowerCase().includes(needle)
    );
  });

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Logo Library</h1>
      <p className="mb-3 text-[12px] text-muted">Top {data?.logos.length ?? 0} logos by usage · thumbnails served via signed URL (15 min expiry)</p>

      <div className="mb-3 max-w-[360px]">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, school, sport…" />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="aspect-square w-full" />)}
        </div>
      ) : filtered.length ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {filtered.map((l) => (
            <div key={l.id} className="flex flex-col rounded-md bg-surface p-2 shadow-card">
              <Thumbnail path={l.thumbnailPath || l.logoPath} alt={l.name} className="aspect-square w-full" />
              <div className="mt-2 truncate text-[11.5px] font-medium" title={l.name}>{l.name || 'Untitled'}</div>
              <div className="truncate text-[10px] text-muted">
                {l.schoolName || ''}{l.sport ? ` · ${l.sport}` : ''}
              </div>
              <div className="mt-0.5 text-[10px] text-subtle">Used {l.usageCount ?? 0}×</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md bg-surface p-8 text-center text-[12px] text-muted shadow-card">No logos match "{q}".</div>
      )}
    </div>
  );
}
